import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "../components/ui/tabs";

// Enums
const QuestionType = {
    STRUCTURE: "structure",
    BRAINSTORM: "brainstorm",
    MATH: "math",
    RECOMMENDATION: "recommendation",
};

const DifficultyLevel = {
    EASY: "easy",
    MEDIUM: "medium",
    HARD: "hard",
};

const CaseType = {
    MARKET_ENTRY: "market_entry",
    PROFITABILITY: "profitability",
    M_AND_A: "m_and_a",
    OPERATIONS: "operations",
};

const Industry = {
    HEALTHCARE: "healthcare",
    TECHNOLOGY: "technology",
    RETAIL: "retail",
    AUTOMOTIVE: "automotive",
    FINANCE: "finance",
};

const CaseFormView = ({ onBack }) => {
    const [formData, setFormData] = useState({
        title: "",
        difficulty: DifficultyLevel.EASY,
        industry: Industry.HEALTHCARE,
        caseType: CaseType.MARKET_ENTRY,
        isInterviewerLed: false,
        expectedTime: "30-45",
        context: "",
        questions: [],
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log("Form submitted:", formData);
        onBack();
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={onBack}>
                    ← Back to Cases
                </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                <Card>
                    <CardHeader>
                        <CardTitle>Case Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div>
                            <Label htmlFor="title">Case Title</Label>
                            <Input
                                id="title"
                                value={formData.title}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        title: e.target.value,
                                    })
                                }
                                placeholder="Enter case title"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="industry">Industry</Label>
                                <select
                                    id="industry"
                                    className="w-full border rounded-md px-3 py-2"
                                    value={formData.industry}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            industry: e.target.value,
                                        })
                                    }>
                                    {Object.entries(Industry).map(
                                        ([key, value]) => (
                                            <option key={key} value={value}>
                                                {value.charAt(0).toUpperCase() +
                                                    value
                                                        .slice(1)
                                                        .toLowerCase()
                                                        .replace("_", " ")}
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>

                            <div>
                                <Label htmlFor="caseType">Case Type</Label>
                                <select
                                    id="caseType"
                                    className="w-full border rounded-md px-3 py-2"
                                    value={formData.caseType}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            caseType: e.target.value,
                                        })
                                    }>
                                    {Object.entries(CaseType).map(
                                        ([key, value]) => (
                                            <option key={key} value={value}>
                                                {value.charAt(0).toUpperCase() +
                                                    value
                                                        .slice(1)
                                                        .toLowerCase()
                                                        .replace("_", " ")}
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="context">Case Context</Label>
                            <Textarea
                                id="context"
                                value={formData.context}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        context: e.target.value,
                                    })
                                }
                                placeholder="Enter case context and background information"
                                className="h-32"
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <Switch
                                id="interviewer-led"
                                checked={formData.isInterviewerLed}
                                onCheckedChange={(checked) =>
                                    setFormData({
                                        ...formData,
                                        isInterviewerLed: checked,
                                    })
                                }
                            />
                            <Label htmlFor="interviewer-led">
                                Interviewer-Led Case
                            </Label>
                        </div>
                    </CardContent>
                </Card>

                <div className="flex justify-end space-x-4">
                    <Button type="button" variant="outline" onClick={onBack}>
                        Cancel
                    </Button>
                    <Button type="submit">Save Case</Button>
                </div>
            </form>
        </div>
    );
};

const CasePreviewCard = ({
    title,
    industry,
    caseType,
    questions,
    expectedTime,
    onEdit,
}) => (
    <Card className="hover:shadow-lg transition-shadow">
        <CardContent className="p-6">
            <div className="space-y-4">
                <div className="flex justify-between items-start">
                    <h3 className="text-lg font-semibold">{title}</h3>
                    <Button variant="ghost" size="sm" onClick={onEdit}>
                        ✎
                    </Button>
                </div>
                <div className="space-y-2">
                    <div className="flex gap-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                            {industry}
                        </span>
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                            {caseType}
                        </span>
                    </div>
                    <p className="text-sm text-gray-600">
                        Questions: {questions}
                    </p>
                    <p className="text-sm text-gray-600">
                        Expected Time: {expectedTime} min
                    </p>
                </div>
            </div>
        </CardContent>
    </Card>
);

const CaseListView = ({
    onNewCase,
    onEditCase,
    searchTerm,
    setSearchTerm,
    filterBy,
    setFilterBy,
}) => {
    const sampleCases = [];

    return (
        <div className="space-y-6">
            <Card>
                <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                                    🔍
                                </span>
                                <Input
                                    placeholder="Search cases..."
                                    value={searchTerm}
                                    onChange={(e) =>
                                        setSearchTerm(e.target.value)
                                    }
                                    className="pl-10"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <select
                                className="border rounded-md px-3 py-2"
                                value={filterBy.industry}
                                onChange={(e) =>
                                    setFilterBy({
                                        ...filterBy,
                                        industry: e.target.value,
                                    })
                                }>
                                <option value="">All Industries</option>
                                {Object.values(Industry).map((industry) => (
                                    <option key={industry} value={industry}>
                                        {industry.charAt(0).toUpperCase() +
                                            industry
                                                .slice(1)
                                                .toLowerCase()
                                                .replace("_", " ")}
                                    </option>
                                ))}
                            </select>
                            <select
                                className="border rounded-md px-3 py-2"
                                value={filterBy.caseType}
                                onChange={(e) =>
                                    setFilterBy({
                                        ...filterBy,
                                        caseType: e.target.value,
                                    })
                                }>
                                <option value="">All Case Types</option>
                                {Object.values(CaseType).map((type) => (
                                    <option key={type} value={type}>
                                        {type.charAt(0).toUpperCase() +
                                            type
                                                .slice(1)
                                                .toLowerCase()
                                                .replace("_", " ")}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Link to="/cases/new">
                    <Card
                        className="cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={onNewCase}>
                        <CardContent className="p-6 flex items-center justify-center h-64">
                            <div className="text-center">
                                <div className="text-4xl text-gray-400 mb-2">
                                    +
                                </div>
                                <p className="mt-2 text-lg font-medium text-gray-600">
                                    Add New Case
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </Link>

                {sampleCases.map((caseItem) => (
                    <CasePreviewCard
                        key={caseItem.id}
                        {...caseItem}
                        onEdit={() => onEditCase(caseItem.id)}
                    />
                ))}
            </div>
        </div>
    );
};

const CasesManagementPage = () => {
    const [view, setView] = useState("list");
    const [searchTerm, setSearchTerm] = useState("");
    const [filterBy, setFilterBy] = useState({
        industry: "",
        caseType: "",
        difficulty: "",
    });

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex justify-between items-center">
                        <h1 className="text-2xl font-bold text-gray-900">
                            Case Interview Management
                        </h1>
                        {view === "list" && (
                            <Button onClick={() => setView("form")}>
                                + New Case
                            </Button>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {view === "list" ? (
                    <CaseListView
                        onNewCase={() => setView("form")}
                        onEditCase={() => setView("form")}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        filterBy={filterBy}
                        setFilterBy={setFilterBy}
                    />
                ) : (
                    <CaseFormView onBack={() => setView("list")} />
                )}
            </main>
        </div>
    );
};

export default CasesManagementPage;
